import { useIntl } from "react-intl";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import QueryKeys from "@app/constants/QueryKeys.ts";
import { useUserService } from "@app/services/api/useUserService.ts";
import { showNotification } from "@app/services/notification/showNotification.tsx";

export const useAvatar = (id: string) => {
  const { updateAvatar, deleteUserAvatar } = useUserService();
  const queryClient = useQueryClient();
  const intl = useIntl();

  const uploadAvatar = useMutation({
    mutationFn: (avatarBase64: File) => {
      const formData = new FormData();
      formData.append("avatarFile", avatarBase64 as Blob);
      return updateAvatar(id, formData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.USER, id] });
      showNotification({
        type: "success",
        title: intl.formatMessage({
          id: "success.title",
          defaultMessage: "Success",
        }),
        description: intl.formatMessage({
          id: "success.avatar.upload.description",
          defaultMessage: "Avatar updated successfully",
        }),
      });
    },
    onError: () => {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.not_found.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.avatar.update.description",
          defaultMessage: "Failed to update avatar",
        }),
      });
    },
  });

  const deleteAvatar = useMutation({
    mutationFn: () => {
      return deleteUserAvatar(id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.USER, id] });
      showNotification({
        type: "success",
        title: intl.formatMessage({
          id: "success.title",
          defaultMessage: "Success",
        }),
        description: intl.formatMessage({
          id: "success.avatar.delete.description",
          defaultMessage: "Avatar deleted successfully",
        }),
      });
    },
    onError: () => {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.not_found.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.avatar.delete.description",
          defaultMessage: "Failed to delete avatar",
        }),
      });
    },
  });

  return {
    uploadAvatar,
    deleteAvatar,
  };
};
